'use client';

import { cn } from '@/app/lib/utils';
import Pill from '@/app/components/ui/Pill';
import { Button } from '@/app/components/ui/Button';
import { Card } from '@/app/components/ui/Card';
import { useTranslations } from 'next-intl';
import { Loader } from 'lucide-react';
import { useIntercom } from '../common/IntercomProvider';

export type PlanTier = {
  id: string;
  code: string;
  type?: string;
  sequence: number;
  name: string;
  price: string;
  period: string;
  totalCredits?: number;
  totalUsers?: number;
  totalPhoneLines?: number;
  totalMailboxes?: number;
  totalLinkedinAccounts?: number;
  creditsLabel?: string;
  ctaLabel?: string;
  ctaVariant?: 'default' | 'outline';
  isCurrent?: boolean;
  status?: string;
  disabled?: boolean;
  isCustom?: boolean;
};

export type PlanComparisonRow = {
  label: string;
  values: string[];
};

type PlansComparisonProps = {
  plans: PlanTier[];
  comparisonRows: PlanComparisonRow[];
  customSetupNote: {
    title: string;
    description: string;
  };
  className?: string;
  onPlanAction?: (plan: PlanTier) => void;
  loadingPlanId?: string | null;
  planColumnClass?: string;
  plansWrapperClass?: string;
  fitToContent?: boolean;
  customSetupNoteClassName?: string;
  actionsDisabled?: boolean;
};

const COMPARE_COLUMN_WIDTH = 146;
const HEADER_HEIGHT = 190;
const ROW_HEIGHT = 52;

function PlanColumn({
  plan,
  comparisonValues,
  currentLabel,
  onPlanAction,
  isLoading = false,
  actionsDisabled = false,
  isFirstPlan = false,
  className,
}: {
  plan: PlanTier;
  comparisonValues: string[];
  currentLabel: string;
  onPlanAction?: (plan: PlanTier) => void;
  isLoading?: boolean;
  actionsDisabled?: boolean;
  isFirstPlan?: boolean;
  className?: string;
}) {
  const tPlanStatus = useTranslations('common.planStatus');
  const { trackEvent } = useIntercom();
  const isCanceled = plan.isCurrent && plan.status === 'canceled';
  const isPaymentFailed = plan.isCurrent && plan.status === 'payment_failed';
  const currentToneClasses = isCanceled
    ? {
        border: 'border-destructive',
        pill: 'border-destructive bg-destructive/10',
        text: 'text-destructive',
        label: tPlanStatus('canceled'),
      }
    : {
        border: 'border-information-base',
        pill: 'border-information-base bg-information-light',
        text: 'text-information-base',
        label: currentLabel,
      };

  return (
    <div
      className={cn(
        'relative flex min-w-[143px] flex-1 basis-0 flex-col border-r border-border',
        !plan.isCurrent && 'last:border-r-0',
        plan.isCurrent && 'border border-t-0',
        plan.isCurrent && isFirstPlan && 'z-10 -ml-px',
        plan.isCurrent && currentToneClasses.border,
        className
      )}
    >
      {plan.isCurrent ? (
        <div
          className={cn(
            'absolute -left-px -right-px top-0 z-10 flex h-7 -translate-y-full items-center justify-center rounded-t-[4px] border',
            currentToneClasses.pill
          )}
        >
          <p className={cn('text-[12px] font-medium leading-4', currentToneClasses.text)}>
            {currentToneClasses.label}
          </p>
        </div>
      ) : null}

      <div
        className="flex flex-col items-start justify-start gap-[10px] border-b border-border px-4 py-4"
        style={{ height: HEADER_HEIGHT }}
      >
        <div className="flex flex-col gap-6">
          <p className="min-h-10 text-[14px] font-medium leading-5 tracking-[-0.084px] text-muted-foreground">
            {plan.name}
          </p>

          <div className="flex min-h-[52px] flex-col items-start gap-0.5">
            <p className="text-[18px] font-medium leading-6 tracking-[-0.27px] text-foreground">
              {plan.price}
            </p>
            <p className="text-[12px] leading-4 text-muted-foreground">
              {plan.period || <span className="invisible">/monthly</span>}
            </p>
          </div>
        </div>

        {plan.ctaLabel ? (
          plan.disabled && !plan.isCustom && !isCanceled && !isPaymentFailed ? (
            <Pill className="h-auto rounded-[4px] border-0 bg-secondary px-3 py-1.5 text-[14px] font-medium leading-5 tracking-[-0.084px] text-disabled-300">
              {plan.ctaLabel}
            </Pill>
          ) : plan.isCustom ? (
            <Button
              type="button"
              variant="outline"
              disabled={actionsDisabled}
              onClick={() => {
                trackEvent('custom_plan_upgrade', {
                  current_path: window.location.pathname,
                });
                window.open('https://calendly.com/1eye/30min', '_blank', 'noopener,noreferrer');
              }}
              className="h-8 rounded-[4px] px-3 text-[14px] font-medium leading-5 tracking-[-0.084px] text-muted-foreground shadow-[0px_1px_2px_0px_rgba(10,13,20,0.03)]"
            >
              {plan.ctaLabel}
            </Button>
          ) : (
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                if (actionsDisabled) {
                  return;
                }

                trackEvent(`${plan.code}_${plan.ctaLabel?.toLowerCase()}`, {
                  current_path: window.location.pathname,
                });
                onPlanAction?.(plan);
              }}
              disabled={
                actionsDisabled || (plan.disabled && !isCanceled && !isPaymentFailed) || isLoading
              }
              className="h-8 rounded-[4px] px-3 text-[14px] font-medium leading-5 tracking-[-0.084px] text-muted-foreground shadow-[0px_1px_2px_0px_rgba(10,13,20,0.03)]"
            >
              {isLoading ? <Loader className="h-4 w-4 animate-spin" /> : null}
              {plan.ctaLabel}
            </Button>
          )
        ) : (
          <div className="h-8" />
        )}
      </div>

      {comparisonValues.map((value, index) => (
        <div
          key={`${plan.name}-${index}`}
          className="flex items-center px-4 py-2 text-[14px] leading-5 tracking-[-0.084px] text-foreground"
          style={{ height: ROW_HEIGHT }}
        >
          {value}
        </div>
      ))}
    </div>
  );
}

export default function PlansComparison({
  plans,
  comparisonRows,
  customSetupNote,
  className,
  onPlanAction,
  loadingPlanId,
  planColumnClass,
  plansWrapperClass,
  fitToContent = false,
  customSetupNoteClassName,
  actionsDisabled = false,
}: PlansComparisonProps) {
  const t = useTranslations('settings.plansPage');

  return (
    <div className={cn('flex flex-col gap-4', className)}>
      <div className={cn('overflow-x-auto overflow-y-visible', fitToContent && 'w-fit max-w-full')}>
        <div className="overflow-visible pt-7">
          <Card
            className={cn(
              'w-max overflow-visible rounded-[4px] border border-border shadow-none',
              !fitToContent && 'min-w-full'
            )}
          >
            <div
              className={cn(
                'grid items-stretch',
                fitToContent
                  ? 'w-max grid-cols-[146px_max-content]'
                  : 'min-w-full grid-cols-[146px_minmax(0,1fr)]'
              )}
            >
              <div
                className="border-r border-border bg-secondary"
                style={{ width: COMPARE_COLUMN_WIDTH, minWidth: COMPARE_COLUMN_WIDTH }}
              >
                <div
                  className="flex flex-col gap-2 border-b border-border bg-secondary p-4"
                  style={{ height: HEADER_HEIGHT }}
                >
                  <p className="text-[14px] font-medium leading-5 tracking-[-0.084px] text-foreground">
                    {t('comparePlans')}
                  </p>
                  <p className="max-w-[94px] text-[12px] leading-4 text-muted-foreground">
                    {t('findRightPlan')}
                  </p>
                </div>

                {comparisonRows.map(row => (
                  <div
                    key={row.label}
                    className="flex items-center bg-secondary px-4 py-2 text-[14px] font-medium leading-5 tracking-[-0.084px] text-foreground"
                    style={{ height: ROW_HEIGHT }}
                  >
                    {row.label}
                  </div>
                ))}
              </div>

              <div className={cn('min-w-0 overflow-visible', plansWrapperClass)}>
                <div className={cn('flex items-stretch', !fitToContent && 'min-w-full')}>
                  {plans.map((plan, index) => (
                    <PlanColumn
                      key={plan.id}
                      plan={plan}
                      className={planColumnClass}
                      currentLabel={t('current')}
                      comparisonValues={comparisonRows.map(row => row.values[index] || '-')}
                      onPlanAction={onPlanAction}
                      isLoading={loadingPlanId === plan.id}
                      actionsDisabled={actionsDisabled}
                      isFirstPlan={index === 0}
                    />
                  ))}
                </div>
              </div>
            </div>
          </Card>
        </div>
      </div>

      <div
        className={cn(
          'flex h-8 items-center gap-1 rounded-[4px] bg-border px-2 py-1 text-[12px] leading-4',
          customSetupNoteClassName
        )}
      >
        <span className="font-medium text-foreground">{customSetupNote.title}</span>
        <span className="text-muted-foreground">
          {customSetupNote.description}{' '}
          <a
            href="https://calendly.com/1eye/30min"
            target="_blank"
            rel="noopener noreferrer"
            className="font-medium text-foreground underline underline-offset-2 hover:opacity-70"
          >
            Book a meeting
          </a>
        </span>
      </div>
    </div>
  );
}
